/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import useSWR, {
  Key,
  SWRConfiguration,
  unstable_serialize,
  useSWRConfig,
} from 'swr';
import { useDebouncedCallback } from 'use-debounce';

export interface ObservableValue<Data> {
  value: Data | undefined;
  done: boolean;
}

type MutatorCallback<Data = any> = (
  currentData?: ObservableValue<Data>
) => ObservableValue<Data> | undefined;

export type SWRObservableOptions<Data = any, Error = any> = {
  next: (
    error?: Error | null,
    data?: ObservableValue<Data> | MutatorCallback<Data>
  ) => void;
};

type SWRObservable<
  SWRSubKey extends Key = Key,
  Data = any,
  Error = any
> = SWRSubKey extends () => infer Arg | null | undefined | false
  ? (key: Arg, { next }: SWRObservableOptions<Data, Error>) => void
  : SWRSubKey extends null | undefined | false
  ? never
  : SWRSubKey extends infer Arg
  ? (key: Arg, { next }: SWRObservableOptions<Data, Error>) => void
  : never;

function processDataFromPreviousData<Data>(
  data: ObservableValue<Data> | MutatorCallback<Data>,
  prevData: ObservableValue<Data>
) {
  if (typeof data === 'function') {
    // Handle function updates
    const dataFn = data as MutatorCallback<Data>;

    // This dataFn call assumes accumulatedDataRef is not nullish, otherwise
    // it will throw an error and return a nullish value under certain circumstances.
    // so we must prepopulate the accumulatedDataRef with value that exists in the cache.
    return dataFn(prevData);
  } else {
    // Handle direct data updates
    return data;
  }
}

// hook for endpoints which stream a finite amount of data.
// unlike useSWRSubscription we support all SWR options,
// namely global mutate & interval polling.
export default function useSWRObservable<
  Data = any,
  Error = any,
  SWRSubKey extends Key = Key
>(
  key: SWRSubKey,
  subscribe: SWRObservable<any, Data, Error>,
  config: SWRConfiguration
) {
  const keepPreviousData = config.keepPreviousData || false;

  const { mutate: globalMutate, cache: globalCache } = useSWRConfig();

  const debouncedMutate = useDebouncedCallback(globalMutate, 100, {
    maxWait: 100,
  });

  const swr = useSWR(
    key,
    (params): Promise<ObservableValue<Data>> => {
      let resolveRef: (data: ObservableValue<Data>) => void;
      let rejectRef: (error: unknown) => void;
      let accFromPrevDataRef = globalCache.get(unstable_serialize(params))
        ?.data as ObservableValue<Data>;

      const promise = new Promise<ObservableValue<Data>>((resolve, reject) => {
        resolveRef = resolve;
        rejectRef = reject;
      });

      let currentStreamDataRef: ObservableValue<Data> = {
        value: undefined,
        done: false,
      };

      const next: SWRObservableOptions<Data, Error>['next'] = (error, data) => {
        if (error != null) {
          rejectRef?.(error);
        } else if (data) {
          if (keepPreviousData && accFromPrevDataRef) {
            // If keepPreviousData is true and we have previous data,
            // accumulate the streaming data but don't update cache until done
            const processedData = processDataFromPreviousData(
              data,
              accFromPrevDataRef
            );
            if (processedData) {
              accFromPrevDataRef = processedData;
            }

            if (accFromPrevDataRef?.done) {
              resolveRef?.(accFromPrevDataRef);
            }
          } else {
            // Accumulate the data manually. SWR has some sort of debounce with mutating
            // it's cache in rapid succession. it waits until the last mutation before
            // sending the update to the FE. We accumulate data then throttle the update
            // call to swr cache.

            const processedData = processDataFromPreviousData(
              data,
              currentStreamDataRef
            );
            if (processedData) {
              currentStreamDataRef = processedData;
            }
            debouncedMutate(params, currentStreamDataRef, false);
            if (currentStreamDataRef?.done) {
              resolveRef?.(currentStreamDataRef);
            }
          }
        }
      };

      subscribe(key, { next });
      return promise;
    },
    {
      ...config,
      onSuccess: (data, ...params) => {
        config.onSuccess?.(data.value, ...params);
      },
    }
  );

  type SWRMutate = (
    data?: Data | Promise<Data | undefined>
  ) => Promise<Data | undefined>;

  const data = swr.data?.value;
  const mutate = useCallback<SWRMutate>(
    (data) => {
      if (data instanceof Promise) {
        return swr
          .mutate(data.then((value) => ({ value, done: true })))
          .then((a) => a?.value);
      }

      if (typeof data === 'undefined') {
        return swr.mutate(undefined, true).then((a) => a?.value);
      }

      return swr.mutate({ value: data, done: true }).then((a) => a?.value);
    },
    [swr]
  );

  return { ...swr, data, mutate };
}
